import { Box, Button, CircularProgress, IconButton, InputAdornment, TablePagination, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { FirstMenu, MenuDish } from '../../../models';
import DialogComponent from '../../../component/dialog/dialog.component';
import MenuListComponent, { MenuListItem } from '../menu-list/menu-list-component';
import i18n from '../../../i18n';
import { Close } from '@mui/icons-material';
import { resolveMenuDishListRowId } from '../../../utils/menu-dish-list-row-id';

export default class MenuDishList extends React.Component<Props, State> {
    public state: State = {
        // 当前选中项目（本地 number item_id；云端可为 string id）
        selectedRowIds: [],
        selectedRows: [],
        // 关键词
        searchValue: '',
        currentPage: 0,
        rowPerPage: 15,
        rowsPerPageOptions: [15, 20, 25, 30]
    }

    /**
     * 菜单树（不含勾选态）缓存：`menuData` 很大时避免每次勾选都全量 map/reduce 重建。
     * 勾选由 `MenuListComponent.checkboxSelectionIds` 驱动重绘。
     */
    private menuStructureCache: {
        menuDataRef: FirstMenu[] | undefined;
        filterKey: string;
        menu: MenuListItem[];
    } | null = null;
    // *********************
    // Life Cycle Function
    // *********************

    componentDidUpdate(prevProps: Props) {
        /** 弹窗常驻挂载、仅 `open` 切换时（如套餐组选菜）：每次打开须清空勾选，避免沿用上一次的选中 */
        if (this.props.open && !prevProps.open) {
            this.setState({
                selectedRowIds: [],
                selectedRows: [],
            });
        }
    }

    // *********************
    // Default Function
    // *********************
    /**
     * 清空选择
     */
    clearSelected() {
        this.setState({
            selectedRowIds: [],
            selectedRows: []
        })
    }

    /**
     * 选中/反选
     * @param menu
     * @param index
     */
    handleClick(menu: MenuListItem) {
        if (menu.type === 'FirstMenu') {
            /** 云端套餐选菜：点击一级菜单时懒加载该菜单详情 */
            void this.props.onExpandFirstMenu?.(menu);
        }
        if (menu.type === 'MenuDish') {
            const { dataSource } = menu;
            let { selectedRowIds, selectedRows } = this.state;
            /** 勾选唯一键：云端 item_id=0 时用云端 id */
            const rowId = resolveMenuDishListRowId(dataSource as MenuDish);
            const index = selectedRowIds.indexOf(rowId);

            if (index < 0) {
                // 单选
                if (this.props.isRadio) {
                    selectedRowIds = [];
                    selectedRows = [];
                }
                selectedRowIds.push(rowId);
                selectedRows.push(dataSource as MenuDish);
            } else {
                selectedRowIds.splice(index, 1);
                selectedRows.splice(index, 1);
            }
            this.setState({
                selectedRows,
                selectedRowIds
            })
        }
    }

    /**
     * 修改搜索关键词
     */
    onChangSearchValue(value: string) {
        this.setState({ searchValue: value, }, async () => {
            await this.props?.getMenuForSearch?.(value.trim());
        });
    }

    private menuStructureFilterKey(props: Props): string {
        return [
            props.filterDishId ?? '',
            (props.filterDishArr ?? []).join(','),
            String(props.isShowPackage),
            String(props.isSellOut),
            String(props.isOnlyPackage),
            props.dishGroupSelectedId ?? '',
            props.expandLoadingCloudMenuId ?? '',
        ].join('\x1e');
    }

    /**
     * 构建列表用菜单树（不写入选中 id，勾选由 `checkboxSelectionIds` 传入列表组件）。
     * 云端懒加载中：对应一级菜单子树临时替换为 LoadingRow。
     */
    private buildUnselectedMenuTree(props: Props): MenuListItem[] {
        const {
            menuData,
            filterDishId,
            filterDishArr,
            isSellOut,
            dishGroupSelectedId,
            isShowPackage,
            isOnlyPackage,
            expandLoadingCloudMenuId,
        } = props;
        const loadingCloudId = String(expandLoadingCloudMenuId ?? '').trim();
        return menuData.map((item) => {
            const isMenuDish = _.get(item, 'type') === 'MenuDish';
            const cloudMenuId = String((item as { id?: string }).id ?? '').trim();
            const isExpandLoading = !!loadingCloudId && cloudMenuId === loadingCloudId;
            return {
                key: `${item.main_group_id}`,
                title: item.main_group_name ?? '',
                type: _.get(item, 'type', 'FirstMenu'),
                originId: isMenuDish ? _.get(item, 'item_id') : undefined,
                children: isMenuDish
                    ? undefined
                    : isExpandLoading
                      ? [
                            {
                                key: `__loading-${cloudMenuId}`,
                                title: i18n.t('general_text_loading'),
                                type: 'LoadingRow',
                                dataSource: {},
                            },
                        ]
                      : (item.menu_item_slu || []).reduce((res: MenuListItem[], subMenuItem) => {
                            if (subMenuItem.dmi_slu_id === dishGroupSelectedId) return res;
                            res.push({
                                key: `sub-${subMenuItem.dmi_slu_id}`,
                                title: subMenuItem.dmi_slu_name ?? '',
                                dataSource: subMenuItem,
                                type: 'SecondMenu',
                                children: (subMenuItem.menu_item || [])
                                    .filter((menuItem) => {
                                        let filterFlag: boolean =
                                            (isShowPackage || menuItem.item_type !== 3) &&
                                            filterDishId !== menuItem.item_id &&
                                            _.indexOf(filterDishArr, menuItem.item_id) === -1;
                                        if (isSellOut) {
                                            filterFlag =
                                                filterDishId !== menuItem.item_id &&
                                                _.indexOf(filterDishArr, menuItem.item_id) === -1;
                                        }
                                        if (isOnlyPackage) {
                                            filterFlag =
                                                menuItem.item_type === 3 && filterDishId !== menuItem.item_id;
                                        }
                                        return filterFlag;
                                    })
                                    .map((menuItem) => {
                                        /** 行唯一 id：云端新建 item_id=0 时用云端 id，避免同 key */
                                        const rowId = resolveMenuDishListRowId(menuItem);
                                        return {
                                            type: 'MenuDish' as const,
                                            key: `menu-${rowId}`,
                                            title: menuItem.item_name1 || '',
                                            secondTitle: menuItem.item_name2 || '',
                                            dataSource: menuItem,
                                            originId: rowId,
                                        };
                                    }),
                            });
                            return res;
                        }, []),
                dataSource: item,
            };
        });
    }

    private getCachedMenuList(): MenuListItem[] {
        const { menuData } = this.props;
        const filterKey = this.menuStructureFilterKey(this.props);
        const c = this.menuStructureCache;
        if (c && c.menuDataRef === menuData && c.filterKey === filterKey) {
            return c.menu;
        }
        const menu = this.buildUnselectedMenuTree(this.props);
        this.menuStructureCache = { menuDataRef: menuData, filterKey, menu };
        return menu;
    }

    // 改变当前页数  下一页 | 上一页
    handleSetPage(event: any, newPage: number) {
        this.setState({
            currentPage: newPage
        })
    }

    // 设置每页显示的...行数
    handleSetRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
        const rowsPerPage = parseInt(event.target.value, 10)
        this.setState({
            rowPerPage: rowsPerPage,
            currentPage: 0
        })
    }

    // *********************
    // View
    // *********************

    render() {
        const { currentPage, rowPerPage, selectedRowIds, selectedRows, searchValue } = this.state;
        const {
            isPagination,
            inputLoading,
            onConfirm,
            onCancel,
            open,
            isSearch,
            isSellOut,
            isOnlyPackage,
            confirmLoading,
            listLoading,
        } = this.props;
        const menu = this.getCachedMenuList();
        const newMenu = menu.slice(currentPage * rowPerPage, currentPage * rowPerPage + rowPerPage).map((item) => item);
        /** 首屏拉菜单列表：居中转圈，避免空列表误显「暂无数据」 */
        const showListLoading = !!listLoading && menu.length === 0;
        return (
            <DialogComponent
                maxWidth='sm'
                heightSize='large'
                open={open}
                sx={{
                    '& .MuiDialog-container > .MuiPaper-root': {
                        background: 'var(--fill-neutral-100)',
                    },
                }}
                onClose={
                    ((event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
                        if (confirmLoading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
                            return;
                        }
                        onCancel();
                    }) as React.ComponentProps<typeof DialogComponent>['onClose']
                }
                defaultHeaderProps={{
                    title: i18n.t('google_text_menu_dish_list'),
                    onClose: confirmLoading ? () => { } : onCancel,
                }}
                footer={
                    <>
                        <Button
                            color='primary'
                            variant='outlined'
                            onClick={onCancel}
                            disabled={!!confirmLoading}
                            sx={{
                                backgroundColor: 'var(--icon-primary-200)',
                                color: 'var(--text-dark)',
                                minWidth: '100px',
                                border: 0,
                                '&:hover': {
                                    backgroundColor: 'var(--icon-primary-200)',
                                    color: 'var(--text-dark)',
                                }
                            }}
                        >{i18n.t('google_text_public_button_cancel')}</Button>
                        <Button
                            color='primary'
                            variant='contained'
                            onClick={() => onConfirm(selectedRowIds, selectedRows)}
                            disabled={!!confirmLoading || showListLoading}
                            startIcon={
                                confirmLoading ? (
                                    <CircularProgress color='inherit' size={18} thickness={4} />
                                ) : undefined
                            }
                            sx={{
                                minWidth: '100px',
                                border: 0
                            }}
                        >{i18n.t('google_text_confirm')}</Button>
                    </>
                }
            >
                <Box hidden={!isSearch} padding={'8px 0'}>
                    <Box width='100%' position='relative'>
                        <TextField
                            variant={'outlined'}
                            fullWidth
                            label={i18n.t('google_text_please_enter_a_keyword')}
                            value={searchValue}
                            onChange={(event) => {
                                this.onChangSearchValue(event.target.value);
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        {
                                            inputLoading &&
                                            <CircularProgress
                                                color='primary'
                                                size={'20px'}
                                            />
                                        }
                                        {
                                            searchValue !== '' &&
                                            <IconButton
                                                size='small'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    this.onChangSearchValue('');
                                                }}
                                            >
                                                <Close />
                                            </IconButton>
                                        }
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                </Box>
                {showListLoading ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        py={6}
                        aria-busy="true"
                        aria-label={i18n.t('general_text_loading')}
                    >
                        <CircularProgress color="primary" size={28} thickness={4} />
                        <Typography variant="body2" color="text.secondary">
                            {i18n.t('general_text_loading')}
                        </Typography>
                    </Box>
                ) : menu.length > 0 ? (
                        <Box>
                            <MenuListComponent
                                fullWidth
                                isHideContextMenu={true}
                                disableDrag
                                isCourseGroup={isOnlyPackage}
                                menu={isPagination ? newMenu : menu}
                                isShowCheckBox={true}
                                checkboxSelectionIds={selectedRowIds}
                                isShowSecondTitle={isSellOut}
                                handleClick={(menu) => {
                                    this.handleClick(menu)
                                }}
                                isHideAddBtn
                            />
                            {
                                // 菜品列表分页组件
                                isPagination &&
                                <TablePagination
                                    rowsPerPageOptions={this.state.rowsPerPageOptions}
                                    labelRowsPerPage={i18n.t('general_row_each_page')}
                                    component='div'
                                    count={menu.length}
                                    rowsPerPage={this.state.rowPerPage}
                                    page={this.state.currentPage}
                                    onPageChange={(e, page) => this.handleSetPage(e, page)}
                                    onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleSetRowsPerPage(event)}
                                />
                            }
                        </Box>
                        ) : (
                        <Box paddingLeft={'16px'}>
                            {i18n.t('google_text_public_text_no_data')}
                        </Box>
                )}
            </DialogComponent >
        )
    }
}

// *********************
// Type
// *********************
type Props = {
    // 开关
    open: boolean;
    // 取消
    onCancel: () => void;
    // 确认（id 为本地 item_id 或云端菜品 string id）
    onConfirm: (selectedRowIds: Array<number | string>, selectedRows: MenuDish[]) => void;
    // 菜单源数据
    menuData: FirstMenu[];
    // 需要剔除的菜品
    filterDishId?: number;
    // 需要剔除的菜品
    filterDishArr?: number[];
    // 单选
    isRadio?: boolean;
    // 搜索
    isSearch?: boolean;
    // 获取关键词菜单
    getMenuForSearch?: (value: string) => Promise<void>;
    // 沽清菜品
    isSellOut?: boolean;
    // 基础信息-套餐区域选择的菜单id
    dishGroupSelectedId?: number | string;
    // 输入框搜索加载
    inputLoading?: boolean;
    /** 确认提交中（如套餐组添加菜品接口请求中） */
    confirmLoading?: boolean;
    // 是否分页
    isPagination?: boolean;
    // 是否展示套餐
    isShowPackage?: boolean;
    // 是否只展示套餐
    isOnlyPackage?: boolean;
    /**
     * 点击一级菜单时回调（如云端套餐选菜懒加载菜单详情）。
     * 展开/收起仍由 `MenuListComponent` 内部处理。
     */
    onExpandFirstMenu?: (menu: MenuListItem) => void | Promise<void>;
    /**
     * 首屏列表 loading（如云端 `GET .../menus`）；为空且 loading 时显示居中转圈，不显示「暂无数据」。
     */
    listLoading?: boolean;
    /**
     * 正在懒加载详情的云端菜单 string id；匹配一级菜单时子树显示 LoadingRow。
     */
    expandLoadingCloudMenuId?: string | null;
}

type State = {
    // 当前选中项目的唯一 id（本地 item_id>0；云端可为 string id）
    selectedRowIds: Array<number | string>;
    // 当前选中的菜品行
    selectedRows: MenuDish[];
    // 搜索关键词
    searchValue: string;
    // 当前页码
    currentPage: number;
    // 一页显示行数
    rowPerPage: number;
    // 每页可选行数选项
    rowsPerPageOptions: number[];
}